import { useRef } from "react";
import { Star, ChevronLeft, ChevronRight, Quote } from "lucide-react";
import { Reveal } from "@/components/portfolio/Reveal";
import { SITE } from "@/lib/site";

export function ClientResults() {
  const trackRef = useRef(null);

  const scroll = (dir) => {
    const el = trackRef.current;
    if (!el) return;
    const card = el.querySelector("[data-card]");
    const w = card ? card.offsetWidth + 16 : 320;
    el.scrollBy({ left: dir * w, behavior: "smooth" });
  };

  return (
    <section className="py-16 md:py-24 border-t border-cream-dark" data-testid="client-results-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <Reveal>
          <div className="flex flex-wrap items-end justify-between gap-6 mb-10">
            <div>
              <p className="eyebrow text-ink-soft mb-5">Client Results</p>
              <h2 className="font-serif text-4xl sm:text-5xl tracking-tight text-ink">Trusted by Clients</h2>
              <p className="mt-3 text-sm text-ink-soft max-w-md leading-relaxed">
                Real outcomes from UGC collaborations — sample cards shown for demonstration.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => scroll(-1)} data-testid="results-prev" aria-label="Previous results"
                className="w-11 h-11 rounded-full border border-ink/20 flex items-center justify-center text-ink hover:bg-ink hover:text-cream transition-colors duration-300">
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button onClick={() => scroll(1)} data-testid="results-next" aria-label="Next results"
                className="w-11 h-11 rounded-full border border-ink/20 flex items-center justify-center text-ink hover:bg-ink hover:text-cream transition-colors duration-300">
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        </Reveal>
      </div>

      <Reveal delay={0.1}>
        <div
          ref={trackRef}
          data-testid="results-slider"
          className="flex gap-4 overflow-x-auto snap-x snap-mandatory no-scrollbar px-6 md:px-[max(3rem,calc((100vw-80rem)/2+3rem))] pb-2"
        >
          {SITE.clientResults.map((c, i) => (
            <article
              key={c.brand}
              data-card
              data-testid={`result-card-${i}`}
              className="w-[280px] sm:w-[320px] shrink-0 snap-start bg-white border border-cream-dark rounded-2xl p-6 flex flex-col hover:border-ink/30 transition-colors duration-300"
            >
              <div className="flex items-center justify-between mb-5">
                <span className="flex items-center gap-0.5" aria-label={`${c.rating} out of 5 stars`}>
                  {[...Array(5)].map((_, s) => (
                    <Star key={s} className={`w-3.5 h-3.5 ${s < c.rating ? "fill-ink text-ink" : "text-ink/20"}`} />
                  ))}
                </span>
                <span className="text-[9px] uppercase tracking-[0.16em] font-semibold border border-ink/15 text-ink-soft px-2.5 py-1 rounded-full" data-testid={`result-sample-${i}`}>
                  Sample
                </span>
              </div>
              <Quote className="w-5 h-5 text-ink/20 mb-3" />
              <p className="text-sm leading-relaxed text-ink" data-testid={`result-made-${i}`}>{c.made}</p>
              <p className="mt-3 text-[13px] leading-relaxed text-ink-soft">
                <span className="text-[10px] uppercase tracking-[0.16em] text-ink-soft/70 block mb-1">How it was used</span>
                {c.used}
              </p>
              <div className="mt-auto pt-5">
                <p className="text-[13px] font-semibold leading-relaxed text-ink bg-cream-dark/70 border-l-2 border-ink px-3.5 py-2.5" data-testid={`result-outcome-${i}`}>
                  {c.result}
                </p>
                <div className="mt-4 flex items-center justify-between">
                  <p className="font-serif text-lg text-ink" data-testid={`result-brand-${i}`}>{c.brand}</p>
                  <p className="text-[10px] uppercase tracking-[0.14em] text-ink-soft">{c.category}</p>
                </div>
              </div>
            </article>
          ))}
        </div>
      </Reveal>
    </section>
  );
}
